import React from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
  Group,
  GroupSeparator,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Text,
} from "@salestrak/ui";

import type { ObjectAccessLevel } from "#app/lib/data";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { getDisplayName } from "#app/lib/utils/get-display-name";
import type { Omittable } from "@salestrak/ui";

export type MentionAccessUser = Omittable<{
  userId: string;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  profileImage?: string | null;
}>;

// Team principal raised by the Teams-property assignment flow — grants go
// through the team share mutations instead of per-user shares.
export type MentionAccessTeam = Omittable<{
  teamId: string;
  teamName: string;
}>;

// Which flow raised the prompt — @-mentions in a note vs adding the
// user as an owner / assignee. Only the copy changes; the grant
// actions are identical.
export type MentionAccessVariant = "mention" | "assignment";

type MentionAccessDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: MentionAccessUser[];
  /** Teams lacking access (Teams-property assignments). Rendered into the
   *  same sentence as the users; grants apply to both. */
  teams?: MentionAccessTeam[];
  // Switches the title/description copy ("mentioned" vs "assigned").
  variant?: MentionAccessVariant;
  // True when the actor can manage sharing on the issue (full_edit).
  // Drives whether the grant buttons render; without it the dialog
  // becomes informational and the author can only proceed or cancel.
  canManageSharing: boolean;
  // Whether a grant call is in flight; locks the buttons + cancel.
  submitting?: boolean;
  // "Do nothing": post the note as-is.
  onDoNothing: () => void;
  // Grant item-level access on this issue to every mentioned user at
  // the chosen access level. Default click on the split button passes
  // `view`; the chevron menu picks one of the higher tiers.
  onGrantItemAccess: (level: ObjectAccessLevel) => void;
  // Grant workflow-level access on the parent workflow at the chosen
  // access level. Same split-button shape as item access.
  onGrantWorkflowAccess: (level: ObjectAccessLevel) => void;
}>;

// "You mentioned @soandso, but they do not have access to this
// project." prompt — pops on note submit when one or more @-mentioned
// users can't see the issue. With full_edit the author gets a split
// button (Grant access) — clicking the label grants `view`; the chevron
// exposes the edit / workflow tiers. Without full_edit, the dialog is
// informational.
export function MentionAccessDialog({
  open,
  onOpenChange,
  users,
  teams = [],
  variant = "mention",
  canManageSharing,
  submitting = false,
  onDoNothing,
  onGrantItemAccess,
  onGrantWorkflowAccess,
}: MentionAccessDialogProps) {
  const { preferences } = useUserSettings();
  const displayFullNames = preferences?.displayFullNames ?? false;

  // Users read as mentions (@name); teams read as plain "the X team".
  const labels = [
    ...users.map((u) => `@${getDisplayName(u, displayFullNames) ?? "user"}`),
    ...teams.map((t) => `the ${t.teamName} team`),
  ];
  const renderedMentions = formatMentionList(labels);
  const isAssignment = variant === "assignment";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isAssignment ? "Assigned without access" : "Mention without access"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            You {isAssignment ? "assigned" : "mentioned"} {renderedMentions},
            but they do not have access to this project.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!canManageSharing ? (
          <AlertDialogPanel>
            <Text size="sm" variant="muted">
              If they should have access, contact your admin.
            </Text>
          </AlertDialogPanel>
        ) : null}

        <AlertDialogFooter>
          {canManageSharing ? (
            <>
              <Button variant="ghost" disabled={submitting} onClick={onDoNothing}>
                Do nothing
              </Button>
              <SplitGrantButton
                label="Grant access"
                // Single dropdown: item-edit escalation + the two
                // workflow-level options. Default click on the main
                // button stays at item `view`.
                options={[
                  {
                    label: "Grant edit access",
                    onClick: () => onGrantItemAccess("edit"),
                  },
                  {
                    label: "Grant view only workflow access",
                    onClick: () => onGrantWorkflowAccess("view"),
                  },
                  {
                    label: "Grant edit workflow access",
                    onClick: () => onGrantWorkflowAccess("edit"),
                  },
                ]}
                disabled={submitting}
                onPrimary={() => onGrantItemAccess("view")}
              />
            </>
          ) : (
            <AlertDialogClose
              render={<Button variant="outline" />}
              disabled={submitting}
              onClick={onDoNothing}
            >
              Got it
            </AlertDialogClose>
          )}
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

// Composed split button: main label triggers the default action, and the
// trailing chevron opens a menu of additional options. Mirrors the "Invite
// members" split button on the /members route — Group + GroupSeparator host
// the two Buttons; the menu portals natively (Base UI stacks it above the
// alert dialog with no portal host).
function SplitGrantButton({
  label,
  options,
  disabled,
  onPrimary,
}: Omittable<{
  label: string;
  options: Array<{ label: string; onClick: () => void }>;
  disabled?: boolean;
  onPrimary: () => void;
}>) {
  return (
    <Menu>
      <Group>
        <Button onClick={onPrimary} disabled={disabled}>
          {label}
        </Button>
        <GroupSeparator />
        <MenuTrigger
          render={
            <Button
              size="icon"
              disabled={disabled}
              aria-label="More grant options"
            />
          }
        >
          <Icon name="chevron-down" />
        </MenuTrigger>
      </Group>
      <MenuPopup align="end" className="min-w-60">
        {options.map((opt, i) => (
          <MenuItem key={`${opt.label}-${i}`} onClick={opt.onClick}>
            {opt.label}
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}

// Oxford-joins pre-formatted labels (callers own the @/"the X team" shape).
function formatMentionList(mentions: string[]): string {
  if (mentions.length === 0) return "";
  if (mentions.length === 1) return mentions[0]!;
  if (mentions.length === 2) return `${mentions[0]} and ${mentions[1]}`;
  return `${mentions.slice(0, -1).join(", ")}, and ${mentions[mentions.length - 1]}`;
}
